import type * as React from 'react';

import type * as api from '@gitbook/api-client';
import { createPalette, usePaletteTrigger } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ListItem,
    ListItemBody,
    ListItemControls,
    ListItemDescription,
    ListItemDetail,
    ListItemIndicator,
} from '@gitbook/spine-react';
import { getChangeRequestSubject } from '@gitbook/util-format';

import { ChangeRequestIcon, useChangeRequestActionsCommands } from '../../../Components';
import { useNavigation } from '../../../navigation';
import { ChangeRequestDetails } from './ChangeRequestDetails';

/**
 * A change request item in the side sheet listing.
 */
export function ChangeRequestItem(props: {
    spaceKey: string;
    changeRequest: api.ChangeRequest;
    onPress?: () => void;
}): React.ReactElement {
    const { spaceKey, changeRequest, onPress: onPressProp } = props;

    const { navigate } = useNavigation();
    const paletteGroup = useChangeRequestActionsCommands({ spaceKey, changeRequest });
    const subject = getChangeRequestSubject(changeRequest);

    const onPress = () => {
        if (onPressProp) {
            onPressProp();
        }

        navigate({
            screen: {
                type: 'space',
                space: spaceKey,
                changeRequest: changeRequest.number,
                revision: undefined,
                sideSheet: { type: 'overview' },
            },
        });
    };

    const paletteTrigger = usePaletteTrigger({
        palette: () =>
            createPalette({
                groups: paletteGroup ? [paletteGroup] : [],
            }),
    });

    return (
        <ListItem selected={paletteTrigger.opened} onPress={onPress}>
            <ListItemIndicator>
                <ChangeRequestIcon changeRequest={changeRequest} size="md" className="mr-1" />
            </ListItemIndicator>
            <ListItemBody>
                <ListItemDescription>{subject}</ListItemDescription>
                <ListItemDetail className="flex gap-4">
                    <ChangeRequestDetails changeRequest={changeRequest} />
                </ListItemDetail>
            </ListItemBody>
            {paletteGroup ? (
                <ListItemControls>
                    <Button
                        {...paletteTrigger.triggerProps}
                        leadingIcon={Icon.MoreVertical}
                        label="More"
                        size="small"
                        kind="nested"
                        iconOnly
                    />
                </ListItemControls>
            ) : null}
        </ListItem>
    );
}
